import { useEffect } from "react";
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { QuoteProvider } from "@/components/QuoteModal";
import { ContentProvider } from "@/content/ContentContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MobileBottomBar from "@/components/MobileBottomBar";
import ChatWidget from "@/components/ChatWidget";
import useLenis from "@/hooks/useLenis";
import Home from "@/pages/Home";
import GrabRails from "@/pages/GrabRails";
import HandRails from "@/pages/HandRails";
import RubberRamps from "@/pages/RubberRamps";
import Gallery from "@/pages/Gallery";
import Reviews from "@/pages/Reviews";
import Contact from "@/pages/Contact";
import SuburbPage from "@/pages/SuburbPage";
import AdminLogin from "@/pages/AdminLogin";
import Admin from "@/pages/Admin";

const LEGACY_REDIRECTS = {
  "/grab-rail-installation-perth": "/grab-rails",
  "/modularhandrails": "/hand-rails",
  "/disability-ramps": "/rubber-ramps",
  "/bathroom-accessibility": "/grab-rails",
  "/services": "/",
  "/whoweare": "/",
  "/projects": "/gallery",
  "/contact-us": "/contact",
};

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.__lenis?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function NotFound() {
  return (
    <div className="mx-auto max-w-7xl px-4 pb-24 pt-40 sm:px-6 lg:px-8" data-testid="not-found-page">
      <h1 className="font-display text-4xl font-extrabold text-charcoal sm:text-5xl">Page not found.</h1>
      <p className="mt-4 text-base text-ink/70">The page you're looking for doesn't exist.</p>
      <Link
        to="/"
        data-testid="not-found-home-link"
        className="mt-8 inline-flex min-h-[48px] items-center rounded-full bg-charcoal px-7 py-3 text-sm font-bold tracking-[0.08em] text-white transition-colors duration-300 hover:bg-ink"
      >
        BACK TO HOME
      </Link>
    </div>
  );
}

export default function App() {
  useLenis();
  return (
    <ContentProvider>
      <QuoteProvider>
        <BrowserRouter>
          <ScrollToTop />
          <div className="App min-h-screen bg-ivory font-body text-ink">
            <Header />
            <main className="pb-14 md:pb-0">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/grab-rails" element={<GrabRails />} />
                <Route path="/hand-rails" element={<HandRails />} />
                <Route path="/rubber-ramps" element={<RubberRamps />} />
                <Route path="/gallery" element={<Gallery />} />
                <Route path="/reviews" element={<Reviews />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/areas/:slug" element={<SuburbPage />} />
                {Object.entries(LEGACY_REDIRECTS).flatMap(([from, to]) => [
                  <Route key={from} path={from} element={<Navigate to={to} replace />} />,
                  <Route key={`${from}-slash`} path={`${from}/`} element={<Navigate to={to} replace />} />,
                ])}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
            <Footer />
            <MobileBottomBar />
            <ChatWidget />
            <Toaster position="top-center" />
          </div>
        </BrowserRouter>
      </QuoteProvider>
    </ContentProvider>
  );
}
